import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const testimonials = [
  {
    name: "Anna K.",
    text: "Świetny występ! Nasi pracownicy byli zachwyceni, a pozytywna energia utrzymywała się jeszcze przez wiele dni.",
  },
  {
    name: "Marek N.",
    text: "Profesjonalne podejście i świetne dostosowanie żartów do naszej branży. Polecam!",
  },
  {
    name: "Katarzyna W.",
    text: "Najlepsza integracja firmowa jaką mieliśmy! Wszyscy do dziś wspominają ten wieczór z uśmiechem.",
  },
  {
    name: "Tomasz B.",
    text: "Rewelacyjny kontakt z publicznością. Komik idealnie wyczuł atmosferę i dopasował materiał do naszego zespołu.",
  },
  {
    name: "Joanna M.",
    text: "Organizowaliśmy galę firmową i występ komediowy był absolutnym hitem wieczoru. Na pewno wrócimy!",
  },
  {
    name: "Paweł S.",
    text: "Bardzo profesjonalna obsługa od pierwszego kontaktu. Występ przeszedł nasze najśmielsze oczekiwania.",
  },
];

const ITEMS_PER_VIEW = 3;

export const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const maxIndex = Math.ceil(testimonials.length / ITEMS_PER_VIEW) - 1;

  const next = useCallback(() => setCurrent(prev => prev >= maxIndex ? 0 : prev + 1), [maxIndex]);
  const prev = useCallback(() => setCurrent(prev => prev <= 0 ? maxIndex : prev - 1), [maxIndex]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const visibleTestimonials = testimonials.slice(
    current * ITEMS_PER_VIEW,
    current * ITEMS_PER_VIEW + ITEMS_PER_VIEW
  );

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-primary via-[#2A2040] to-primary text-white relative overflow-hidden">
      <div className="absolute w-[400px] h-[400px] bg-brand-purple/10 rounded-full -top-40 -right-40 blur-3xl" />
      <div className="absolute w-[300px] h-[300px] bg-brand-magenta/10 rounded-full -bottom-40 -left-40 blur-3xl" />

      <div className="container relative z-10">
        <ScrollReveal>
          <h2 className="text-4xl md:text-5xl font-montserrat font-extrabold text-center mb-16">
            Co mówią nasi <span className="gradient-text">klienci</span>?
          </h2>
        </ScrollReveal>

        <div className="relative max-w-5xl mx-auto">
          {/* Navigation arrows */}
          <button
            onClick={prev}
            className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Poprzednia opinia"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Następna opinia"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="grid md:grid-cols-3 gap-6"
            >
              {visibleTestimonials.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-500 flex flex-col min-h-[280px]"
                >
                  <Quote className="w-8 h-8 text-brand-purple/50 mb-4 flex-shrink-0" />
                  <div className="flex mb-5 gap-1 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-brand-orange" fill="currentColor" />
                    ))}
                  </div>
                  <p className="font-inter mb-6 text-gray-300 leading-relaxed flex-grow">{testimonial.text}</p>
                  <div className="border-t border-white/10 pt-4 flex-shrink-0">
                    <p className="font-montserrat font-bold text-white">{testimonial.name}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === current ? "bg-brand-purple w-8" : "bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Strona ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
